"use client";

import { useState, useTransition } from "react";
import { followTheCall, unfollowTheCall } from "@/app/actions/the-call";
import type { FavouriteSpotType } from "@/app/actions/favourites";
import type { CallSport } from "@/lib/the-call/types";
import type { SupProfileKey } from "@/lib/conditions/sport-profiles";
import {
  callPreferenceOptionsForCategory,
  type CallPreferenceCategory,
} from "./call-preference-options";

export type SavedSpotCallPreferenceConfig = {
  spotName: string;
  category: CallPreferenceCategory;
  allowedSupProfiles?: readonly SupProfileKey[];
};

export function SavedSpotCallPreferences({
  spotId,
  spotType,
  spotName,
  category,
  initialCallSports = [],
  allowedSupProfiles = [],
}: {
  spotId: string;
  spotType: FavouriteSpotType;
  spotName: string;
  category: CallPreferenceCategory;
  initialCallSports?: string[];
  allowedSupProfiles?: readonly SupProfileKey[];
}) {
  const [override, setOverride] = useState<Record<string, boolean>>({});
  const [pendingSport, setPendingSport] = useState<CallSport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const options = callPreferenceOptionsForCategory(category, allowedSupProfiles);

  function following(sport: CallSport): boolean {
    return override[sport] ?? initialCallSports.includes(sport);
  }

  function toggleSport(sport: CallSport) {
    const next = !following(sport);
    setError(null);
    setOverride((prev) => ({ ...prev, [sport]: next }));
    setPendingSport(sport);

    startTransition(async () => {
      const result = next
        ? await followTheCall(spotId, spotType, sport)
        : await unfollowTheCall(spotId, sport);

      if (!result.success) {
        setOverride((prev) => ({ ...prev, [sport]: !next }));
        setError(result.error ?? "Could not save The Call preference.");
      }
      setPendingSport(null);
    });
  }

  return (
    <div className="relative z-20 mt-3 w-full max-w-[320px] rounded-xl border border-white/20 bg-navy/95 p-3 text-white shadow-xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/60">
        The Call preferences
      </p>
      <p className="mt-1 text-[12px] leading-snug text-white/80">
        Choose what PeakWave should watch at {spotName}. Saving alone does not turn on alerts.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => {
          const active = following(option.sport);
          const pending = isPending && pendingSport === option.sport;
          return (
            <button
              key={option.sport}
              type="button"
              onClick={() => toggleSport(option.sport)}
              disabled={pending}
              className={[
                "min-h-[44px] rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60",
                active
                  ? "border-gold/60 bg-gold/20 text-gold-light"
                  : "border-white/20 bg-white/10 text-white/85 hover:bg-white/20",
              ].join(" ")}
            >
              {pending ? "Saving" : option.label}
            </button>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[11px] leading-snug text-amber-200">
          {error}
        </p>
      )}
    </div>
  );
}
